import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { createEditor, Editor, Node, Range, Transforms } from 'slate';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';

import SectionDescriptionData from './SectionDescriptionData';

const messages = defineMessages({
  sectionDescription: {
    id: 'Type the section description…',
    defaultMessage: 'Type the section description…',
  },
});

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const Edit = (props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    onAddBlock,
    onChangeBlock,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    selected,
  } = props;

  const [editor] = useState(withReact(createEditor()));
  const [initialValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: data?.['description'] || '' }],
    },
  ]);

  const intl = useIntl();
  const prevSelected = usePrevious(selected);
  const text = useMemo(() => data?.['description'] || '', [data]);
  const placeholder = useMemo(
    () =>
      data.placeholder || intl.formatMessage(messages['sectionDescription']),
    [data.placeholder, intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  // Visual igual ao View
  const { align = 'center' } = data;
  const hClass = cx(
    {
      [`text-${align}`]: align,
    },
    'section-description',
  );
  const renderElement = useCallback(
    ({ attributes, children }) => {
      return (
        <div {...attributes} className={hClass}>
          {children}
        </div>
      );
    },
    [hClass],
  );

  useEffect(() => {
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        ReactEditor.focus(editor);
      } else {
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [prevSelected, selected, editor]);

  useEffect(() => {
    const oldText = Node.string(editor);
    if (oldText !== text) {
      Transforms.insertText(editor, text, {
        at: [0, 0],
      });
    }
  }, [editor, text]);

  const handleChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== text) {
      onChangeBlock(block, { ...data, description: newText });
    }
  }, [editor, text, onChangeBlock, block, data]);

  const handleKeyDown = useCallback(
    (ev) => {
      if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          onSelectBlock(onAddBlock('text', index + 1));
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        onFocusPreviousBlock && onFocusPreviousBlock(block, blockNode?.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        onFocusNextBlock && onFocusNextBlock(block, blockNode?.current);
      }
    },
    [
      index,
      blockNode,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const handleFocus = useCallback(() => {
    onSelectBlock && onSelectBlock(block);
  }, [block, onSelectBlock]);

  if (typeof window !== 'undefined' && window.__SERVER__) {
    return <div />;
  }

  return (
    <>
      <Slate
        editor={editor}
        onChange={handleChange}
        initialValue={initialValue}
      >
        <Editable
          readOnly={!editable}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          onFocus={handleFocus}
          renderElement={renderElement}
        />
      </Slate>
      <SidebarPortal selected={selected}>
        <SectionDescriptionData
          key={block}
          {...props}
          data={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

Edit.propTypes = {
  block: PropTypes.string,
  onChangeBlock: PropTypes.func,
  selected: PropTypes.bool,
  blockNode: PropTypes.any,
  detached: PropTypes.bool,
  editable: PropTypes.bool,
  index: PropTypes.number,
  onAddBlock: PropTypes.func,
  onFocusNextBlock: PropTypes.func,
  onFocusPreviousBlock: PropTypes.func,
  onSelectBlock: PropTypes.func,
  data: PropTypes.object,
};

Edit.defaultProps = {
  detached: false,
  editable: true,
};

export default Edit;
